import React from 'react'
import AndroidLiveActivityCard from './AndroidLiveActivityCard'
import IosLiveActivityCard from './IosLiveActivityCard'
import { deriveTrack, getFullTrack, isConfirmedOnly } from './track'
import type { PreviewContent, PreviewPlatform, TrackerStage } from './types'

export interface LiveActivityCardProps {
  content: PreviewContent
  platform: PreviewPlatform
  // Whether the group can configure delivery — required (with a
  // delivery-section focus) for the 4-step delivery track to show.
  deliveryCapable?: boolean
  // The merchant's uploaded logo; falls back to the brand-red placeholder
  // square when omitted (no logo uploaded).
  appIconUrl?: string
  // Merchant display name, shown in the Android header caption
  // ("{merchantName} · {eta}"). Optional: when absent the caption is the ETA
  // alone. Unused by the iOS card (its header is fixed OS chrome).
  merchantName?: string
  // Tracker stage keys whose statuses are ALL disabled — dropped as a middle
  // track step (the first/last step always renders regardless).
  disabledStages?: Set<TrackerStage>
}

// The Live Activity preview card — a thin platform dispatcher. Track
// derivation (full track, disabled-stage filtering, confirmed-only, active
// index) happens once here; the platform-specific rendering lives in
// IosLiveActivityCard (dark glass card, dot-and-connector tracker) and
// AndroidLiveActivityCard (light notification card, segmented bar). The active
// step is the track stage matching content.stage (an out-of-track/canceled
// stage highlights nothing). {location_name} renders literally.
const LiveActivityCard = ({
  content,
  platform,
  deliveryCapable = false,
  appIconUrl,
  merchantName,
  disabledStages = new Set(),
}: LiveActivityCardProps) => {
  const fullTrack = getFullTrack(content, deliveryCapable)
  const track = deriveTrack(fullTrack, disabledStages)
  const confirmedOnly = isConfirmedOnly(fullTrack, disabledStages)
  // A stage outside this track (e.g. a canceled status) highlights no step (no
  // completed dots) rather than defaulting to "Placed" — keeps an out-of-track
  // stage from mislabeling the progress bar.
  const activeIndex = track.indexOf(content.stage)

  const platformProps = {
    content,
    appIconUrl,
    track,
    activeIndex,
    confirmedOnly,
  }

  return platform === 'ios' ? (
    <IosLiveActivityCard {...platformProps} />
  ) : (
    <AndroidLiveActivityCard
      {...platformProps}
      merchantName={merchantName}
      isCanceled={activeIndex === -1}
    />
  )
}

export default LiveActivityCard
